import { FC, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { 
  Box, Typography, Grid
} from "@mui/material"
import { useForm, Controller, FormProvider } from "react-hook-form"
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import AccessTariffSelectField from "../../customFields/AccessTariffSelectField";
import { useTranslation } from "react-i18next";
import { RootState } from "@/store";
import { FormHelperText } from "@mui/material";
import GasProductSelectField from "../../customFields/GasProductSelectField";
import useLangs from "@/languages/hooks/useLangs";
import { IStepForm, StepGasContractFormData } from "../../../types/stepsFormData";
import { SubmitButtonContext } from "../../../Contractation";
import { useGasProducts } from "../../../hooks/data/useProducts";

const FormGasContract: FC<IStepForm<StepGasContractFormData>> = ({
  onSubmit
}) => {
  const submitButtonContext = useContext(SubmitButtonContext);
  
  const { t } = useTranslation();
  const { currentLang } = useLangs();

  const contractation = useSelector((state: RootState) => state[`gasContractation`]);

  const onAboutGasContractSubmit = async (values: StepGasContractFormData) => {
    await onSubmit(values);
  };

  const schema = z.object({
    accessTariff: z.object({
      id: z.number().nonnegative({message: 'common:text.required_field'}),
      name: z.string().optional(),
    }),
    selectedProduct: z.number({
      invalid_type_error: t('common:text.required_field')
    }).positive({message: t('common:text.required_field')}),
    selectedProductName: z.string().optional(),
  }) satisfies z.ZodType<StepGasContractFormData>;

  const methods = useForm<StepGasContractFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...contractation.steps.gasContract,
      ...((
        contractation.steps.installation.cupsSIPSData?.accessTariff &&
        contractation.steps.gasContract.accessTariff.id < 0
      ) &&
         {
        accessTariff: {
          id: contractation.steps.installation.cupsSIPSData.accessTariff,
          name: contractation.steps.installation.cupsSIPSData.accessTariffName
        }
      })
    }
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting }
  } = methods;

  const watchedAccessTariff = watch("accessTariff");

  const productsData = useGasProducts({
    accessTariff: watchedAccessTariff.id, 
    cups: contractation.steps.installation.cups, 
    lang: currentLang
  });

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => {handleSubmit(onAboutGasContractSubmit)()},
      loading: isSubmitting,
      disabled: !!(Object.keys(errors).length)
    });
  }, [isSubmitting, !!(Object.keys(errors).length)]);

  useEffect(() => {
    if(productsData.isSuccess && productsData.data.length) {
      const currentSelectedProductId = productsData.data.find(prod => prod.id === getValues("selectedProduct"))?.id;
      if(currentSelectedProductId) {
        setValue("selectedProduct", currentSelectedProductId);
      } else {
        setValue("selectedProduct", -1);
      }
    }
  }, [productsData.data]);

  return (
    <FormProvider {...methods}>
      <form data-testid="form-step" onSubmit={handleSubmit(onAboutGasContractSubmit)}>

        <Typography variant="h6" color="primary">
          {t('common:text.contractation_current_gas_contract_data')}
        </Typography>
        <Typography variant="body2" sx={{mb: 2}}>
          {t('common:text.contractacion_about_installation_info')}
        </Typography>
        <Box sx={{ml: {md: 5}, mr: {md: 5}}}>

          {(!contractation.steps.gasContract.sipsData || 
            !(watchedAccessTariff.id >= 0)) && 
            <>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {t('common:text.contract_title')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={8} md={6} mb={2}>
                <AccessTariffSelectField
                  gas={true}
                  // Can be set to null if the SIPS does not return data
                  readOnly={typeof contractation.steps.installation?.cupsSIPSData?.accessTariff  == "number" &&
                    contractation.steps.installation?.cupsSIPSData?.accessTariff >= 0}
                  label={t('common:text.contractation_access_tariff')}
                />
              </Grid>

            </>
          }
        </Box>

        {watchedAccessTariff.id >= 0 &&
          <>
            <Typography variant="h6" color="primary">
              {t('common:text.contractation_new_pricelist')}
            </Typography>
            <Typography variant="body2" sx={{mb: 2}}>
              {t('common:text.contractation_current_pressure_warning')}
            </Typography>
            <Box sx={{ml:{md: 5}, mr:{md: 5}}}>
              <Controller
                name={"selectedProduct"}
                control={control}
                render={({ field }) => <GasProductSelectField
                  availableProducts={productsData.isSuccess ? productsData.data : []}
                  loading={productsData.isLoading}
                  input={{
                    ...field,
                    onChange: (value) => {
                      field.onChange(value);
                      setValue("selectedProductName", productsData.data?.find(
                        prod => (prod.id === value))?.name
                      )
                    }
                  }}
                />}
              />
              {!!(errors.selectedProduct) &&
                <FormHelperText error={true}>
                  {errors.selectedProduct.message}
                </FormHelperText>
              }
            </Box>
          </>
        }
      </form>
    </FormProvider>
  );
};

export default FormGasContract;
